import { useCallback } from 'react';
import { AIRDemoPanel } from '@frontend/ai-receptionist-sales-demo';
import {
  AIRSettingsChangedPayload,
  AIRTestPanelEventPayload,
  useChannelSubscription,
  type QueueEvent,
} from '@frontend/queue';
import { openSchedulePulse, openSchedulePulseRequest, SchedulePulseTab } from '@frontend/schedule';
import { useAppScopeStore } from '@frontend/scope';
import { GlobalSettingsPanelEntry, useGlobalSettingsPanel } from '@frontend/settings-panel-engine';

/**
 * ## SETTINGS GLOBAL PANEL REGISTRATION
 *
 * This file is used to register global panels that can be opened from anywhere in the app.
 * Simply export your global panel component here and it will be registered automatically.
 */

const AIRTestSettingsPanel = () => {
  const { open, close } = useGlobalSettingsPanel('ai-receptionist-test-panel');
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0];

  const handleViewAppointmentRequest = useCallback(
    (requestId: string) => {
      if (requestId) {
        openSchedulePulseRequest(locationId, requestId);
      } else {
        openSchedulePulse(SchedulePulseTab.AppointmentRequests);
      }
    },
    [locationId]
  );

  const eventHandler = useCallback(
    (event: QueueEvent<AIRTestPanelEventPayload | AIRSettingsChangedPayload>) => {
      if (event.payload?.action === 'open') {
        open();
        return true;
      }
      if (event.payload?.action === 'close') {
        close();
        return true;
      }
      if (event.payload?.action === 'settings-changed') {
        // mark the event as handled to avoid warning of unhandled event in console. This won't impact other subscribers
        return true;
      }
      return false;
    },
    [open, close]
  );

  useChannelSubscription('ai-receptionist-test-panel', eventHandler);

  return (
    <GlobalSettingsPanelEntry id='ai-receptionist-test-panel' width='medium'>
      <AIRDemoPanel onClose={close} onViewAppointmentRequest={handleViewAppointmentRequest} />
    </GlobalSettingsPanelEntry>
  );
};

export const AiReceptionistTestGlobalPanel = AIRTestSettingsPanel;
